// Pairing: the QR link, the HMAC proof that the phone saw the secret, and the SAS code typed at the desk.
// docs/protocol.md, "Pairing".
import { b64u, buf, fromB64u, fromUtf8, ProtocolError, utf8 } from "./encoding.js";
const subtle = () => globalThis.crypto.subtle;
export function encodePairLink(appOrigin, link) {
    return `${appOrigin.replace(/\/+$/, "")}/pair#v1.${b64u(utf8(JSON.stringify(link)))}`;
}
export function decodePairLink(hash) {
    const m = /^#?v1\.([A-Za-z0-9_-]+)$/.exec(hash);
    if (!m)
        throw new ProtocolError("bad_link", "not a v1 pairing link");
    let link;
    try {
        link = JSON.parse(fromUtf8(fromB64u(m[1])));
    }
    catch {
        throw new ProtocolError("bad_link", "unreadable pairing link");
    }
    for (const k of ["a", "m", "p", "s", "f", "r"]) {
        if (typeof link[k] !== "string" || !link[k])
            throw new ProtocolError("bad_link", `missing ${k}`);
    }
    if (!/^https:\/\//.test(link.r) && !/^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(link.r)) {
        throw new ProtocolError("bad_link", "relay must be https");
    }
    return link;
}
export function pairMacMessage(pairingId, devSigPub, devEncPub, deviceName) {
    return utf8(`whistle-pair-v1|${pairingId}|${devSigPub}|${devEncPub}|${deviceName}`);
}
async function hmacKey(secret, usage) {
    return subtle().importKey("raw", buf(fromB64u(secret)), { name: "HMAC", hash: "SHA-256" }, false, [usage]);
}
export async function pairMac(secret, pairingId, devSigPub, devEncPub, deviceName) {
    const key = await hmacKey(secret, "sign");
    return b64u(await subtle().sign("HMAC", key, buf(pairMacMessage(pairingId, devSigPub, devEncPub, deviceName))));
}
/** Constant-time check (WebCrypto's HMAC verify). */
export async function verifyPairMac(secret, mac, pairingId, devSigPub, devEncPub, deviceName) {
    try {
        const key = await hmacKey(secret, "verify");
        return await subtle().verify("HMAC", key, buf(fromB64u(mac)), buf(pairMacMessage(pairingId, devSigPub, devEncPub, deviceName)));
    }
    catch {
        return false;
    }
}
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/**
 * The 6-character SAS, shown on the phone and typed at the desk: "XXX-XXX".
 * Covers both parties' signing AND encryption keys, so a party holding the secret cannot swap the phone's encPub.
 */
export async function sasCode(secret, machine, device) {
    const text = `whistle-sas-v2|${secret}|${machine.sigPub}|${machine.encPub}|${device.sigPub}|${device.encPub}`;
    const h = new Uint8Array(await subtle().digest("SHA-256", buf(utf8(text))));
    // First 30 bits -> six 5-bit symbols.
    const bits = ((h[0] << 22) | (h[1] << 14) | (h[2] << 6) | (h[3] >> 2)) >>> 0;
    let out = "";
    for (let i = 5; i >= 0; i--)
        out += CROCKFORD[(bits >>> (i * 5)) & 31];
    return `${out.slice(0, 3)}-${out.slice(3)}`;
}
/** Normalise what a person typed: case-insensitive, dashes and spaces optional, O->0, I/L->1 (Crockford). */
export function normaliseSas(typed) {
    const s = typed.toUpperCase().replace(/[\s-]/g, "").replace(/O/g, "0").replace(/[IL]/g, "1");
    return s.length === 6 ? `${s.slice(0, 3)}-${s.slice(3)}` : s;
}
/** Constant-time string comparison for codes of equal length. */
export function sameCode(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++)
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return diff === 0;
}
// Signed relay control messages: exact strings, so every implementation agrees.
export const relayAuthText = (accountId, id, nonce) => `whistle-relay-auth-v1|${accountId}|${id}|${nonce}`;
export const createAccountText = (sigPub, encPub, timestamp) => `whistle-create-v1|${sigPub}|${encPub}|${timestamp}`;
export const addDeviceText = (accountId, deviceId) => `whistle-add-device-v1|${accountId}|${deviceId}`;
export const removeDeviceText = (accountId, deviceId) => `whistle-remove-device-v1|${accountId}|${deviceId}`;
//# sourceMappingURL=pairing.js.map